import React, { lazy, useMemo } from 'react';
import ParentStyled from './style';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { loadStyle } from 'globalutils/fmUtils';
import { getDataSets } from './config';

const layout = window?.$environment?.CURRENT_LAYOUT;
const LineChart = lazy(() => import(`globalcomponents/UI/Graph/LineGraphCSA/${layout}`));
const BarChart = lazy(() => import(`globalcomponents/UI/Graph/BarGraphCSA/${layout}`));
let Styled = loadStyle(ParentStyled, 'pages/web/Dashboard/CSASnapshot');

const Component = ({ csasnapshot, ...rest }) => {
  const data = csasnapshot.basicMeasures.series;
  const driverDataFinal = csasnapshot.driverDetails;
  const theme = useTheme();
  const i18n = useTranslation();

  const formatDataForCrash = (data = []) =>
    data.reduce(
      (acc, { monthsBefore, federalRecordable, towAway, injuries, fatalities, citationsIssued }) => {
        acc.labels.push(`${monthsBefore}months`);
        acc.dot.push(federalRecordable);
        acc.tow.push(towAway);
        acc.injuries.push(injuries);
        acc.fatalities.push(fatalities);
        acc.citations.push(citationsIssued);
        return acc;
      },
      { labels: [], dot: [], tow: [], injuries: [], fatalities: [], citations: [] }
    );

  const crashSummaryChart = formatDataForCrash(csasnapshot?.crashSummmary);

  const { datasets1, datasets2, datasets3 } = useMemo(() => {
    return getDataSets({ csasnapshot, crashSummaryChart });
  }, [csasnapshot, crashSummaryChart]);

  const formatDate = (input) => {
    const datePart = input.match(/\d+/g),
      year = datePart[0],
      month = datePart[1],
      day = datePart[2];
    return day + '/' + month + '/' + year;
  };

  const { totalWithin, totalBeyond, totalDriver, finalDate } = useMemo(
    () => ({
      totalWithin:
        driverDataFinal?.Total_Drivers_Interstate_Less_100_Miles + driverDataFinal?.Total_Drivers_Intrastate_Less_100_Miles,
      totalBeyond:
        driverDataFinal?.Total_Drivers_Interstate_More_100_Miles + driverDataFinal?.Total_Drivers_Intrastate_More_100_Miles,
      totalDriver: driverDataFinal?.Total_Drivers_Interstate + driverDataFinal?.Total_Drivers_Intrastate,
      finalDate: driverDataFinal?.MCS150_Date
        ? formatDate(`${driverDataFinal.MCS150_Date}`.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1/$2/$3'))
        : '',
    }),
    [driverDataFinal]
  );

  return (
    <Styled.Wrapper>
      <div className="basicMeasures dashboard_csa_wrapper">
        <div className="panelHeading">Basic Measures</div>
        <div className="panel-content measures">
          <div className="chartContainer">
            {data && data.length !== 0 && (
              <LineChart
                borderColor={'white'}
                width={'450'}
                height={'350'}
                labels={csasnapshot?.basicMeasures?.Dates}
                areagraph
                theme={theme}
                data={datasets1}
                maindata={data}
                id={'legend11'}
              ></LineChart>
            )}
            {data && data.length === 0 && (
              <LineChart
                borderColor={'white'}
                width={'450'}
                height={'350'}
                labels={csasnapshot?.basicMeasures?.Dates}
                areagraph
                theme={theme}
                data={datasets1}
                maindata={data}
                id={'legend11'}
              ></LineChart>
            )}
          </div>
          <div className="chartContainer">
            {data && data.length !== 0 && (
              <LineChart
                borderColor={'white'}
                width={'450px'}
                height={'350px'}
                labels={csasnapshot?.basicMeasures?.Dates}
                data={datasets2}
                areagraph
                theme={theme}
                maindata={data}
                id={'legend21'}
              ></LineChart>
            )}
            {data && data.length === 0 && (
              <LineChart
                borderColor={'white'}
                width={'450px'}
                height={'350px'}
                labels={csasnapshot?.basicMeasures?.Dates}
                data={datasets2}
                areagraph
                theme={theme}
                maindata={data}
                id={'legend21'}
              ></LineChart>
            )}
          </div>
        </div>
      </div>
      <div className="middleWrapper crashSummary">
        <div className="section">
          <div className="panelHeading">
            Crash Summary
            <span className="sub-heading basicheading-description">(based on Inspection results for the past 36 months)</span>
          </div>

          <div className="chartContainerMiddle">
            <BarChart
              borderColor={'white'}
              width={'450px'}
              height={'350px'}
              labels={crashSummaryChart.labels}
              data={datasets3}
              theme={theme}
            ></BarChart>
          </div>
        </div>
        <div className="section">
          <div className="panelHeading">
            {' '}
            Driver Details
            <span className="sub-heading basicheading-description">{finalDate && `(based on MCS-150 dated ${finalDate})`}</span>
          </div>
          <div className="chartContainerMiddle TableWrapper">
            {driverDataFinal && Object.keys(driverDataFinal).length === 0 && <label>No data available</label>}

            {driverDataFinal && Object.keys(driverDataFinal).length !== 0 && (
              <table id="csadriver_details" className="display table" cellSpacing="0" width="100%">
                <thead>
                  <tr>
                    <th></th>
                    <th>Within 100 mi</th>
                    <th>Beyond 100 mi</th>
                    <th>Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr className="driver-interstate">
                    <td># Drivers Interstate</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Interstate_Less_100_Miles}</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Interstate_More_100_Miles}</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Interstate}</td>
                  </tr>
                  <tr className="driver-intrastate">
                    <td># Drivers Intrastate</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Intrastate_Less_100_Miles}</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Intrastate_More_100_Miles}</td>
                    <td align="center">{driverDataFinal.Total_Drivers_Intrastate}</td>
                  </tr>
                  <tr className="driver-total">
                    <td>TOTAL</td>

                    <td align="center">{totalWithin}</td>
                    <td align="center">{totalBeyond}</td>
                    <td align="center">{totalDriver}</td>
                  </tr>
                  <tr className="trip-lease">
                    <td># CDL Drivers: {driverDataFinal.Total_Drivers_Commercialy_Licensed}</td>
                    <td align="center"># Drivers Total: {driverDataFinal.Total_Drivers}</td>
                    <td colspan="2" align="center">
                      Avg. # Trip Lease Drivers/Mo: {driverDataFinal.Average_Trip_Leased_Drivers_Per_Month}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <div className="bottomWrapper dashboard_csa_wrapper inspections">
        <div className="panelHeading">
          Inspections Summary
          <span className="sub-heading inspection-description">
            (based on 24 months prior to:{' '}
            {new Date().getDate() -
              1 +
              '/' +
              (new Date().getMonth() + 1 < 10 ? '0' + (new Date().getMonth() + 1) : new Date().getMonth() + 1) +
              '/' +
              new Date().getFullYear()}
            )
          </span>
        </div>
        <div className="panel-content bottomBoxWrapper inspection-summary">
          {csasnapshot?.inspectionSummary.map((item, index) => {
            return (
              <div className="inspection">
                <div className="overall-inspection">{item.type}</div>
                <div className="inspection-content">
                  <p>Insp: {item.inspections}</p>
                  <p>OOS: {item.oos}</p>
                  <br />
                  <br />
                  <p>% OOS: {item.oosPercent.toFixed(2)}%</p>
                  <p>Nat'l Avg: {item.nationalAverage.toFixed(2)}%</p>
                  <p>Diff: {item.difference.toFixed(2)}%</p>
                </div>
              </div>
            );
          })}
          {csasnapshot?.inspectionSummary.length === 0 && <label>{i18n.t('common.noDataToPlot')}</label>}
        </div>
      </div>
    </Styled.Wrapper>
  );
};

export default Component;
